import {
	Autocomplete,
	Box,
	CircularProgress,
	Divider,
	Grid2,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { WriteLockTooltip } from "@/shared/components/WriteLockTooltip";
import { useOperationalWritesLocked } from "@/shared/hooks/useOperationalWritesLocked";
import type {
	BilletWorkOrderRow,
	BilletWorkOrdersPickerWorkOrder,
} from "../models/BilletWorkOrders";

const sectionHeaderSx = {
	color: "primary.main",
	fontWeight: 600,
	fontSize: "0.85rem",
	mt: 1.5,
	mb: 0.5,
};

const readOnlyFieldProps = {
	size: "small" as const,
	fullWidth: true,
	disabled: true,
	slotProps: { inputLabel: { shrink: true } },
};

const toDisplay = (v: unknown): string => {
	if (v === null || v === undefined) return "";
	return String(v);
};

// Format a date/datetime field preferring the raw UTC value (returned by the
// optimized new SP) and falling back to the SP-formatted *String (returned by
// the legacy proxy path, with extra fractional-seconds precision that we
// truncate to the seconds column).
const fmtDateTime = (raw: string | null | undefined): string =>
	raw ? dxtToLocalServerTime(raw, "yyyy-MM-dd HH:mm:ss") : "";

const formatDecimal3 = (v: unknown): string => {
	if (v === null || v === undefined || v === "") return "";
	const n = Number(v);
	if (!Number.isFinite(n)) return "";
	return n.toFixed(3);
};

interface Props {
	row: BilletWorkOrderRow | null;
	workOrders: BilletWorkOrdersPickerWorkOrder[];
	isSaving: boolean;
	onWorkOrderChange: (workOrderId: number | null) => void;
	onClose: () => void;
}

export const BilletWorkOrderDetailViewer = ({
	row,
	workOrders,
	isSaving,
	onWorkOrderChange,
	onClose,
}: Props) => {
	const writesLocked = useOperationalWritesLocked();
	if (!row) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a billet to view details</Typography>
			</Box>
		);
	}

	const selectedWorkOrder =
		row.WorkOrderId == null
			? null
			: (workOrders.find((w) => w.Id === row.WorkOrderId) ?? null);

	return (
		<Box sx={{ p: 1.5, height: "100%", overflow: "auto" }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						Billet #{row.Id}
					</Typography>
					{isSaving && <CircularProgress size={16} />}
				</Box>
				<Tooltip title="Close">
					<IconButton size="small" onClick={onClose} disabled={isSaving}>
						<CloseIcon />
					</IconButton>
				</Tooltip>
			</Box>

			<Divider sx={{ mb: 1 }} />

			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Production</Typography>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Start"
						value={fmtDateTime(row.Start)}
						{...readOnlyFieldProps}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="End"
						value={fmtDateTime(row.End)}
						{...readOnlyFieldProps}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Production Date"
						value={fmtDateTime(row.ProductionDate)}
						{...readOnlyFieldProps}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Campaign Start"
						value={fmtDateTime(row.CampaignStart)}
						{...readOnlyFieldProps}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Product</Typography>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Product"
						value={toDisplay(row.ProductName)}
						{...readOnlyFieldProps}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Billet Size"
						value={toDisplay(row.BilletSize)}
						{...readOnlyFieldProps}
					/>
				</Grid2>
				<Grid2 size={{ xs: 6, md: 3 }}>
					<TextField
						label="Length"
						value={toDisplay(row.Length)}
						{...readOnlyFieldProps}
					/>
				</Grid2>
				<Grid2 size={{ xs: 6, md: 3 }}>
					<TextField
						label="Billet Weight"
						value={toDisplay(row.BilletWeight)}
						{...readOnlyFieldProps}
					/>
				</Grid2>
				<Grid2 size={{ xs: 6, md: 3 }}>
					<TextField
						label="Start Temp"
						value={toDisplay(row.StartTemperature)}
						{...readOnlyFieldProps}
					/>
				</Grid2>
				<Grid2 size={{ xs: 6, md: 3 }}>
					<TextField
						label="End Temp"
						value={formatDecimal3(row.EndTemperature)}
						{...readOnlyFieldProps}
					/>
				</Grid2>
				<Grid2 size={{ xs: 6, md: 3 }}>
					<TextField
						label="Shift"
						value={toDisplay(row.CrewId)}
						{...readOnlyFieldProps}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Assignment</Typography>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<WriteLockTooltip locked={writesLocked} fullWidth>
						<Autocomplete
							size="small"
							fullWidth
							options={workOrders}
							getOptionLabel={(o) => o.Name}
							value={selectedWorkOrder}
							onChange={(_, newValue) =>
								onWorkOrderChange(newValue?.Id ?? null)
							}
							isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
							disabled={isSaving || writesLocked}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Work Order"
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							)}
						/>
					</WriteLockTooltip>
				</Grid2>
			</Grid2>
		</Box>
	);
};
